<template>
  <div class="discovery">
    <div class="header">
      <img src="@/assets/images/ai-flag.svg" alt="">
      Discovery
    </div>
    <div 
      :style="{ height: processHeight + 'px' }"
      class="searching">
      <div class="searching-header">
        <img class="searching-icon" src="@/assets/images/discovery-icon.svg" alt="">
        <div class="searcing-text">
          <div>·</div>
          <div>{{ searchingText }}</div>
        </div>
        <!-- 展开收起按钮 -->
        <div 
          v-if="searchComplete"
          @click="handleFold"
          class="fold-icon">
          <img 
            :style="{
              transform: isFold ? 'rotate(180deg)' : 'rotate(0deg)'
            }"
            src="@/assets/images/fold.svg" 
            alt="">
        </div>
      </div>
      <div v-if="showProcess" class="searching-content">
        <div 
          v-for="(item, index) in processStep"
          :key="index"
          class="processing-item">
          <div class="processing-item-icon">
            <img v-if="item.status === 0" src="@/assets/images/circle.svg" alt="">
            <img v-else-if="item.status === 1" class="processing" src="@/assets/images/processing-circle.svg" alt="">
            <img v-else src="@/assets/images/complete.svg" alt="">
          </div>
          <div 
            :class="{ 'success': item.status === 2 }"
            class="processing-item-text">
            {{ item.text }}
          </div>
        </div>
      </div>
    </div>
    <div 
      v-if="showThinking" 
      :style="{ 
        marginTop: isFold ? '28px' : '20px', 
        paddingLeft: isFold ? '8px' : '16px'
      }"
      class="thinking-content">
      <div 
        ref="foldRef"
        class="thinking-content-fold">
        <div class="thinking-title">
          <img src="@/assets/images/complete-with-bg.svg" alt="">
          Deep thinking process
        </div>
        <div class="thinking-text">
          {{ thinkingText }}
        </div>
        <div v-if="showSecondThinking" class="second-thinking-content">
          <div class="thinking-title">
            <img src="@/assets/images/complete-with-bg.svg" alt="">
            Result for AI deep analyzing
          </div>
          <div class="looking-for">{{ secondThinkingText }}</div>
          <!-- 商品名称 -->
          <div class="keywords">
            <img src="@/assets/images/looking-for.svg" alt="">
            {{ discoveryResult?.product_main_name?.en }}
          </div>
        </div>
      </div>
      <!-- 仅在折叠时显示 -->
      <div 
        v-if="isFold"
        class="search-result">
        <img :src="productImageBase64" alt="">
        <!-- 商品名称 -->
        <div class="result">{{ discoveryResult?.product_main_name?.en }}</div>
      </div>
      <div 
        v-if="thinkingLoading"
        class="thinking-loading">
        <div class="dot dot-1"></div>
        <div class="dot dot-2"></div>
        <div class="dot dot-3"></div>
        <div class="dot dot-4"></div>
        <div class="dot dot-5"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, watch, computed, onBeforeUnmount } from 'vue';
import { useProcess } from '../hooks/useProcess'
import { useFold } from '../hooks/useFold'
import { aiFetch } from '@/utils/aiFetch'
import { useStore } from 'vuex'

const emits = defineEmits(['discoverySuccess', 'collapseImage'])

const store = useStore()

const { isFold, foldRef, handleFold } = useFold()
const { showProcess, processStep } = useProcess()

const processHeight = ref(40) // 流程区高度

const showThinking = ref(false) // 是否显示思考内容

const showSecondThinking = ref(false) // 是否显示第二段思考内容

const thinkingText = ref('')
const secondThinkingText = ref('')

const thinkingLoading = ref(true) // 思考loading

const searchingText = ref('Searching and evaluating product ...')

const searchComplete = ref(false) // 搜索完成

const productImageBase64 = ref('') // 产品图

// 记录所有定时器id
const timers = ref([])

// AbortController 用于取消请求
const currentAbortController = ref(null)

// 清理所有定时器的函数
const clearAllTimers = () => {
  timers.value.forEach(timer => clearTimeout(timer));
  timers.value = [];
};

// 取消当前所有请求
const cancelCurrentRequests = () => {
  if (currentAbortController.value) {
    currentAbortController.value.abort()
    currentAbortController.value = null
  }
}

// 封装setTimeout，自动记录timer ID
const setTimer = (callback, delay) => {
  const timerId = setTimeout(callback, delay);
  timers.value.push(timerId);
  return timerId;
};

// 初始化数据
const initData = () => {
  showThinking.value = false
  showSecondThinking.value = false
  thinkingText.value = ''
  secondThinkingText.value = ''
  thinkingLoading.value = true
  searchingText.value = 'Searching and evaluating product ...'
  searchComplete.value = false
  processHeight.value = 40
  isFold.value = false
  showProcess.value = false
  processStep.value.forEach(item => item.status = 0)
  clearAllTimers()
  cancelCurrentRequests()
}

// 将图片地址转为base64
const imageToBase64 = (imageUrl) => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'Anonymous'
    img.src = imageUrl
    img.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      canvas.width = img.width
      canvas.height = img.height
      ctx.drawImage(img, 0, 0)
      const dataUrl = canvas.toDataURL('image/png')
      resolve(dataUrl)
    }
  })
} 


const discoveryText = ref('')
watch(discoveryText, (value) => {
  if (value) {
    processStep.value[2].status = 2
    showProcess.value = false
    processHeight.value = 40

    handleFirstThinking()
    getDiscoveryResult()
  }
})

const discoveryResult = ref(null)
watch(discoveryResult, (value) => {
  if (value) {
    handleSecondThinking()
  }
})

// 获取ai discovery的结果
const getDiscoveryResult = async () => {
  try {
    // 取前十个subject
    const productList = JSON.parse(localStorage.getItem('aiDiscoveryProductList'))

    const subjectList = productList.map(item => item.subject)
    const res = await aiFetch('/ai_discovery', {
      method: 'POST',
      body: JSON.stringify({
        image_describe: discoveryText.value,
        similar_products: subjectList
      })
    }, {}, currentAbortController.value)
    discoveryResult.value = res.discovery_result
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log('Discovery result request was cancelled')
      return
    }
    throw error
  }
}

const startDiscovery = async (params) => {
  initData()
  
  // 创建新的 AbortController
  currentAbortController.value = new AbortController()
  
  processHeight.value = 124
  showProcess.value = true
  handleFakeProcess()

  try {
    const mainImageBase64 = await imageToBase64(params.mainImage)
    productImageBase64.value = params.productImage
    
    const res = await aiFetch('/ai_discovery_image_analysis', {
      method: 'post',
      body: JSON.stringify({
        scene_base64: mainImageBase64,
        product_base64: params.productImage
      })
    }, {}, currentAbortController.value)
    
    discoveryText.value = res
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log('Image analysis request was cancelled')
      return
    }
    throw error
  }
}

// 执行假过程
const handleFakeProcess = () => {
  processStep.value[0].status = 2
  processStep.value[1].status = 1
  setTimer(() => {
    processStep.value[1].status = 2
    processStep.value[2].status = 1
  }, 1500)
}

// 第一段思考
const handleFirstThinking = () => {
  searchingText.value = 'Selecting by business insights ...'
  showThinking.value = true // 开始思考
  emits('collapseImage')
  typewriter(discoveryText.value, thinkingText)
}

// 第二段思考
const handleSecondThinking = () => {
  const text = 'The user is looking for'
  showSecondThinking.value = true
  typewriter(text, secondThinkingText).then(async() => {
    emits('discoverySuccess', discoveryResult.value)
    searchingText.value = 'Search complete'
    thinkingLoading.value = false
    searchComplete.value = true
    handleFold() // 收起
  })
}

// 打字机效果
const activeTypewriterInterval = ref(null)
const typewriter = (text, fields) => {
  return new Promise((resolve) => {
    if (activeTypewriterInterval.value) {
      clearInterval(activeTypewriterInterval.value)
      activeTypewriterInterval.value = null
    }

    if (fields.value) {
      fields.value = ''
    }

    let index = 0
    const parent = document.querySelector('.ai-discovery')

    const interval = setInterval(() => {
      if (index < text.length) {
        if (fields) {
          fields.value += text[index]

          // 滚动到最底部
          nextTick(() => {
            if (parent) {
              if (parent.scrollHeight > parent.clientHeight) {
                parent.scrollTo({
                  top: parent.scrollHeight - parent.clientHeight,
                  behavior: 'smooth'
                })
              }
            }
          })
        }
        index++
      } else {
        clearInterval(interval)
        resolve(true)
      }
    }, 10)

    // 保存当前interval，以便后续清理
    activeTypewriterInterval.value = interval;
    // 同时放入总timer列表
    timers.value.push(interval);
  })  
}

defineExpose({
  startDiscovery,
  cancelCurrentRequests
})

onBeforeUnmount(() => {
  clearAllTimers()
  cancelCurrentRequests()
})
</script>

<style lang='less' scoped>
.discovery {
  .header {
    display: flex;
    align-items: center;
    color: #232323;
    font-size: 18px;
    font-family: mon-700;

    img {
      position: relative;
      top: -1px;
    }
  }

  .searching {
    position: relative;
    border-radius: 4px;
    background-color: #EFF4FD;
    padding: 0 11px 0 4px;
    transition: height 0.3s ease-in-out;

    .searching-header {
      display: flex;
      align-items: center;
      gap: 4px;
      margin-top: 12px;
      height: 40px;

      .fold-icon {
        position: absolute;
        top: 50%;
        right: 12px;
        transform: translateY(-50%);
        width: 24px;
        height: 24px;
        cursor: pointer;
        background-color: transparent;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
          background-color: #DFE8FB;
          transition: all 0.3s ease-in-out;
        }

        img {
          width: 16px;
          height: 16px;
          transition: all 0.3s ease-in-out;
        }
      }
    }

    .searcing-text {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      font-family: mon-600;
      color: #232323;
    }
  }

  .searching-content {
    padding-left: 39px;
    .processing-item {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 4px;
      &:last-child {
        margin-bottom: 0;
      }
      .processing-item-icon {
        .processing {
          animation: processing 1s linear infinite;
        }

        @keyframes processing {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      }
      .processing-item-text {
        color: #b2b7c2;
        font-size: 11px;
        font-family: mon-500;
        &.success {
          color: #232323;
        }
      }
    }
  }

  .thinking-content {
    padding-left: 16px;
    border-left: 2px solid #ebebeb;

    .thinking-content-fold {
      overflow: hidden;
      transition: height 0.3s ease-in-out;
    }

    .thinking-title {
      display: flex;
      align-items: center;
      gap: 4px;
      color: #232323;
      font-size: 12px;
      font-family: mon-500;
    }

    .thinking-text {
      margin-top: 12px;
      color: #6e717a;
      font-size: 12px;
      font-family: mon-500;
      line-height: 16px;
    }

    .second-thinking-content {
      margin-top: 24px;

      .looking-for {
        color: #6e717a;
        font-size: 12px;
        font-family: mon-500;
        margin-top: 12px;
      }

      .keywords {
        margin-top: 8px;
        width: fit-content;
        height: 32px;
        border-radius: 16px;
        background-color: #ebf1f5;
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 0 12px;
        color: #232323;
        font-size: 12px;
        font-family: mon-600;
      }
    }

    .thinking-loading {
      display: flex;
      align-items: center;
      margin-top: 16px;
      padding-left: 8px;
      gap: 2px;
      width: 74px;
      height: 24px;
      
      .dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: #232323;
        animation: fade 1.8s infinite cubic-bezier(0.45, 0.05, 0.55, 0.95);
        
        &.dot-1 { animation-delay: 0s; }
        &.dot-2 { animation-delay: 0.2s; }
        &.dot-3 { animation-delay: 0.4s; }
        &.dot-4 { animation-delay: 0.6s; }
        &.dot-5 { animation-delay: 0.8s; }
      }
    }

    @keyframes fade {
      0%, 100% {
        opacity: 1;
        transform: scale(1);
      }
      50% {
        opacity: 0.2;
        transform: scale(0.85);
      }
    }

    .search-result {
      display: flex;
      height: 24px;
      align-items: center;

      img {
        width: 32px;
        height: 32px;
        border-radius: 2px;
        margin-right: 8px;
      }

      .result {
        color: #232323;
        font-size: 14px;
        font-family: mon-600;
        text-decoration: underline;
      }
    }
  }
}
</style>